import { Stack } from "@mui/material";
import { useParams } from "react-router-dom";
import { useGetCampaignSummaryQuery, useGetPlaybookQuery } from "../api";
import { AppRoutes, topicTitlesMapper } from "../types/enums";
import { NavigationBreadcrumbs, OopsBlock } from "../components/ui";
import React, { useEffect, useState } from "react";
import { ToTopBtn } from "../components/ui/buttons";
import { CustomLoader } from "../components/ui/CustomLoader";
import { PlaybookHeader } from "../components/company/plan";
import {
  CRMNotes,
  DoDontBlock,
  FirstCallScriptBlock,
  LeadAmnesiaBestPracticesBlock,
  LeadDetailsBlock,
  OutboundSequenceBlock,
  PreOutreachResearchBlock,
  ReadFirstBlock,
  TextBlock,
} from "../components/company/plan/component";
import { PlaybookNavigationBlock } from "../components/company/plan/component/PlaybookNavigationBlock";
import { useNotFoundRedirect } from "../hooks";
import { useGetPlaybookTitlesList } from "../hooks/useGetPlaybookTitlesList";

const PlaybookPage = () => {
  const { member_id, campaign_id } = useParams();
  const [showScrollTop, setShowScrollTop] = useState<boolean>(false);

  const {
    data: playbook,
    isLoading: isPlaybookLoading,
    error,
  } = useGetPlaybookQuery({
    member_id: member_id!,
  });

  const topicsList = useGetPlaybookTitlesList(playbook);

  useNotFoundRedirect({ error });

  const { data: summary } = useGetCampaignSummaryQuery({
    campaign_id: campaign_id!,
  });

  useEffect(() => {
    const el = document.body;
    if (el) {
      const handleScroll = () => {
        const { top } = el.getBoundingClientRect();
        setShowScrollTop(top <= -54);
      };

      window.addEventListener("scroll", handleScroll);

      return () => window.removeEventListener("scroll", handleScroll);
    }
  }, []);

  if (isPlaybookLoading) {
    return <CustomLoader />;
  }

  if (!playbook || error) {
    return <OopsBlock campaign_id={campaign_id!} />;
  }

  const {
    description,
    lead_details,
    amnesia_best_practices,
    crm_notes,
    do_dont,
    lead_company,
    lead_name,
    company_type,
    outbound_sequence,
    first_call,
    read_first,
    outreach_research,
  } = playbook;

  const isDemo = summary?.is_demo;

  const breadcrumbs = [
    {
      title: summary?.product_name || "",
      path: `${AppRoutes.CAMPAINGS}/${campaign_id}`,
    },
    {
      title: "Accounts",
      path: `${AppRoutes.ACCOUNTS_LIST}/${campaign_id}`,
    },
    {
      title: lead_company,
    },
  ];

  return (
    <Stack
      sx={{
        px: { sm: 8, md: 8, lg: 10, xl: 20 },
        pt: 4,
        minHeight: "Calc(100vh - 74px)",
      }}
      gap="40px"
    >
      <Stack gap="24px">
        <NavigationBreadcrumbs breadcrumbs={breadcrumbs} />
        <Stack flexDirection="row" gap="40px" position="sticky">
          <PlaybookNavigationBlock topicsList={topicsList} isDemo={isDemo} />
          <Stack gap="32px" flexGrow={1} paddingBottom="48px">
            <PlaybookHeader
              isDemo={isDemo}
              isFixed={showScrollTop}
              companyType={company_type}
              companyName={lead_company}
              leadName={lead_name}
              member_id={member_id!}
              campaign_id={campaign_id!}
              playbook={playbook}
            />
            {description && <TextBlock text={description} />}
            {read_first && (
              <ReadFirstBlock
                topic={topicTitlesMapper.read_first}
                {...read_first}
                lead_company={lead_company}
              />
            )}
            {do_dont && <DoDontBlock topic={topicTitlesMapper.do_dont} />}
            {lead_details && (
              <LeadDetailsBlock
                topic={topicTitlesMapper.lead_details}
                leadDetails={lead_details}
              />
            )}

            {outreach_research && (
              <PreOutreachResearchBlock
                topic={topicTitlesMapper.outreach_research}
                {...outreach_research}
                lead_company={lead_company}
              />
            )}

            {outbound_sequence && (
              <OutboundSequenceBlock
                topic={topicTitlesMapper.outbound_sequence}
                {...outbound_sequence}
              />
            )}

            {first_call && (
              <FirstCallScriptBlock
                topic={topicTitlesMapper.first_call}
                {...first_call}
              />
            )}

            {amnesia_best_practices && (
              <LeadAmnesiaBestPracticesBlock
                topic={topicTitlesMapper.amnesia_best_practices}
                {...amnesia_best_practices}
              />
            )}

            {crm_notes && (
              <CRMNotes topic={topicTitlesMapper.crm_notes} list={crm_notes} />
            )}
          </Stack>
        </Stack>
      </Stack>
      {showScrollTop && <ToTopBtn />}
    </Stack>
  );
};

export default PlaybookPage;
